// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabase';

const EMPTY_FORM = {
  customer_name: '',
  contact_name: '',
  email: '',
  phone: '',
  phone_secondary: '',
  billing_street: '',
  billing_city: '',
  billing_province: '',
  billing_postal: '',
  service_street: '',
  service_city: '',
  service_province: '',
  service_postal: '',
  payment_terms_days: 30,
  internal_notes: '',
};

export default function CustomersPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [jobCounts, setJobCounts] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sameAsBilling, setSameAsBilling] = useState(false);

  // Postal code auto-populate (US + Canada) — free API, no key needed
  const lookupPostal = async (postalCode, prefix) => {
    if (!postalCode || postalCode.length < 3) return;
    const clean = postalCode.replace(/\s/g, '').toUpperCase();
    // Detect country: Canadian postal codes start with a letter
    const isCanadian = /^[A-Z]\d/.test(clean);
    const lookupCode = isCanadian ? clean.substring(0, 3) : clean;
    const country = isCanadian ? 'CA' : 'US';
    try {
      const res = await fetch(`https://api.zippopotam.us/${country}/${lookupCode}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.places && data.places.length > 0) {
        const place = data.places[0];
        setForm(prev => ({
          ...prev,
          [`${prefix}_city`]: place['place name'] || prev[`${prefix}_city`],
          [`${prefix}_province`]: place['state abbreviation'] || place['state'] || prev[`${prefix}_province`],
        }));
      }
    } catch { /* silent — postal lookup is best-effort */ }
  };

  const load = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const { data, error: fetchErr } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', user.company_id)
        .eq('is_active', true)
        .order('customer_name');

      if (fetchErr) throw fetchErr;
      setCustomers(data || []);

      // Get job counts per customer
      const { data: jobs } = await supabase
        .from('master_jobs')
        .select('customer_id')
        .eq('company_id', user.company_id)
        .eq('is_deleted', false);

      const counts = {};
      (jobs || []).forEach((j) => {
        counts[j.customer_id] = (counts[j.customer_id] || 0) + 1;
      });
      setJobCounts(counts);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setError('');
    setSameAsBilling(false);
    setModalOpen(true);
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      customer_name: customer.customer_name || '',
      contact_name: customer.contact_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      phone_secondary: customer.phone_secondary || '',
      billing_street: customer.billing_street || '',
      billing_city: customer.billing_city || '',
      billing_province: customer.billing_province || '',
      billing_postal: customer.billing_postal || '',
      service_street: customer.service_street || '',
      service_city: customer.service_city || '',
      service_province: customer.service_province || '',
      service_postal: customer.service_postal || '',
      payment_terms_days: customer.payment_terms_days || 30,
      internal_notes: customer.internal_notes || '',
    });
    setError('');
    setSameAsBilling(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setError('');
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.customer_name.trim()) {
      setError('Customer name is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        company_id: user.company_id,
        customer_name: form.customer_name.trim(),
        contact_name: form.contact_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        phone_secondary: form.phone_secondary.trim() || null,
        billing_street: form.billing_street.trim() || null,
        billing_city: form.billing_city.trim() || null,
        billing_province: form.billing_province.trim() || null,
        billing_postal: form.billing_postal.trim() || null,
        service_street: form.service_street.trim() || null,
        service_city: form.service_city.trim() || null,
        service_province: form.service_province.trim() || null,
        service_postal: form.service_postal.trim() || null,
        payment_terms_days: parseInt(form.payment_terms_days) || 30,
        internal_notes: form.internal_notes.trim() || null,
      };

      if (editing) {
        const { error: updateErr } = await supabase
          .from('customers')
          .update(payload)
          .eq('id', editing.id)
          .eq('company_id', user.company_id);
        if (updateErr) throw updateErr;
      } else {
        payload.is_active = true;
        payload.created_by = user.id;
        const { error: insertErr } = await supabase
          .from('customers')
          .insert(payload);
        if (insertErr) throw insertErr;
      }

      closeModal();
      await load();
    } catch (err) {
      console.error('Save customer error:', err);
      setError(err.message || 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  };

  const filtered = customers.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.customer_name?.toLowerCase().includes(q) ||
      c.contact_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.service_city?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">{customers.length} active customer{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>Add New Customer</button>
      </div>

      <div className="page-body">
        {/* Search */}
        <div className="search-bar" style={{ marginBottom: 20 }}>
          <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>Search</span>
          <input
            placeholder="Search customers by name, contact, email, phone, or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}
            >
              X
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            {search ? 'No customers match your search.' : 'No customers yet. Add your first customer to get started.'}
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>City</th>
                    <th>Jobs</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
                      <td style={{ fontWeight: 600 }}>{c.customer_name}</td>
                      <td>{c.contact_name || '-'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.email || '-'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.phone || '-'}</td>
                      <td>{c.service_city || c.billing_city || '-'}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
                          {jobCounts[c.id] || 0}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h2 className="modal-title">{editing ? 'Edit Customer' : 'New Customer'}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {editing ? 'Update customer details below.' : 'Enter the new customer details below.'}
            </p>

            {error && (
              <div style={{
                background: '#DC354515', color: '#DC3545', padding: '10px 14px',
                borderRadius: 8, marginBottom: 16, fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Customer Name *</label>
                <input
                  className="input"
                  value={form.customer_name}
                  onChange={(e) => handleChange('customer_name', e.target.value)}
                  placeholder="Company or customer name"
                />
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input
                    className="input"
                    value={form.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="Primary contact"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="input"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="input"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Primary phone"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Secondary Phone</label>
                  <input
                    className="input"
                    value={form.phone_secondary}
                    onChange={(e) => handleChange('phone_secondary', e.target.value)}
                    placeholder="Secondary phone"
                  />
                </div>
              </div>

              {/* Billing Address */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, marginTop: 8 }}>
                BILLING ADDRESS
              </div>
              <div className="form-group">
                <input
                  className="input"
                  value={form.billing_street}
                  onChange={(e) => handleChange('billing_street', e.target.value)}
                  placeholder="Street"
                />
              </div>
              <div className="grid-3">
                <div className="form-group">
                  <input
                    className="input"
                    value={form.billing_city}
                    onChange={(e) => handleChange('billing_city', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <input
                    className="input"
                    value={form.billing_province}
                    onChange={(e) => handleChange('billing_province', e.target.value)}
                    placeholder="Province"
                  />
                </div>
                <div className="form-group">
                  <input
                    className="input"
                    value={form.billing_postal}
                    onChange={(e) => handleChange('billing_postal', e.target.value)}
                    onBlur={(e) => lookupPostal(e.target.value, 'billing')}
                    placeholder="Postal Code"
                  />
                </div>
              </div>

              {/* Service Address */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
                  SERVICE ADDRESS
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSameAsBilling(checked);
                      if (checked) {
                        setForm(prev => ({
                          ...prev,
                          service_street: prev.billing_street,
                          service_city: prev.billing_city,
                          service_province: prev.billing_province,
                          service_postal: prev.billing_postal,
                        }));
                      }
                    }}
                    style={{ width: 16, height: 16, accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  Same as billing
                </label>
              </div>
              <div className="form-group">
                <input
                  className="input"
                  value={form.service_street}
                  onChange={(e) => { handleChange('service_street', e.target.value); setSameAsBilling(false); }}
                  placeholder="Street"
                  disabled={sameAsBilling}
                  style={sameAsBilling ? { opacity: 0.6, background: 'var(--bg)' } : {}}
                />
              </div>
              <div className="grid-3">
                <div className="form-group">
                  <input
                    className="input"
                    value={form.service_city}
                    onChange={(e) => { handleChange('service_city', e.target.value); setSameAsBilling(false); }}
                    placeholder="City"
                    disabled={sameAsBilling}
                    style={sameAsBilling ? { opacity: 0.6, background: 'var(--bg)' } : {}}
                  />
                </div>
                <div className="form-group">
                  <input
                    className="input"
                    value={form.service_province}
                    onChange={(e) => { handleChange('service_province', e.target.value); setSameAsBilling(false); }}
                    placeholder="Province"
                    disabled={sameAsBilling}
                    style={sameAsBilling ? { opacity: 0.6, background: 'var(--bg)' } : {}}
                  />
                </div>
                <div className="form-group">
                  <input
                    className="input"
                    value={form.service_postal}
                    onChange={(e) => { handleChange('service_postal', e.target.value); setSameAsBilling(false); }}
                    onBlur={(e) => { if (!sameAsBilling) lookupPostal(e.target.value, 'service'); }}
                    placeholder="Postal Code"
                    disabled={sameAsBilling}
                    style={sameAsBilling ? { opacity: 0.6, background: 'var(--bg)' } : {}}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Payment Terms (days)</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={form.payment_terms_days}
                    onChange={(e) => handleChange('payment_terms_days', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Internal Notes</label>
                <textarea
                  className="input"
                  rows={3}
                  value={form.internal_notes}
                  onChange={(e) => handleChange('internal_notes', e.target.value)}
                  placeholder="Internal notes about this customer..."
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Customer' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
